/**
 * ThornhavenTownSquare - Central landmark: the great Yggdrasil tree + open plaza
 *
 * Builds Thornhaven's permanent coordinate anchor at world origin (0,0,0):
 *   - ONE large ancient/gnarled "Tree of Life" (Quaternius stylized TwistedTree),
 *     scaled up as a major landmark visible across the whole square.
 *   - A clear circular stone-paved plaza around it (CC0 RockPath tiles), with
 *     open walking space kept around the trunk.
 *
 * Everything references registry ASSET IDs (never hardcoded public/ paths).
 * Collision is a single tight collar around the trunk base only; the rest of
 * the plaza is fully walkable so buildings can face/connect inward to this hub.
 *
 * NOTE: hotel / apartments / shops / props / radar markers / spawn points are
 * intentionally NOT built here - this file is the tree + plaza layout only.
 */

import { useMemo } from 'react';
import { useGLTF, Clone } from '@react-three/drei';
import { RigidBody, CylinderCollider } from '@react-three/rapier';
import { assetUrl } from '../../../assets/thornhavenAssetRegistry';

// --- Registry asset IDs (Quaternius Stylized Nature, CC0) ---
const ASSET = {
  tree: 'nature.stylized-nature.twistedtree_1',
  pathWide: 'nature.stylized-nature.rockpath_round_wide',
  pathSmall: 'nature.stylized-nature.rockpath_round_small_1',
};
const URL = {
  tree: assetUrl(ASSET.tree),
  pathWide: assetUrl(ASSET.pathWide),
  pathSmall: assetUrl(ASSET.pathSmall),
};
Object.values(URL).forEach((u) => u && useGLTF.preload(u));

// --- Landmark tree geometry ---
// TwistedTree_1: trunk base sits at the model origin (measured X~0.1, Z~0.0),
// canopy reaches ~15u tall and leans toward +X. TREE_SCALE makes the crimson
// world-tree tower over the ~9u perimeter walls as a landmark.
const TREE_SCALE = 1.55;
const TREE_OFFSET = [0, 0, 0]; // trunk base already centered at model origin

// --- Plaza (stone-paved disc around the trunk) ---
const PLAZA_RADIUS = 12;     // outer radius of paved plaza
const TRUNK_CLEAR = 2.6;     // keep a bare ring of walking space around trunk
const TILE_STEP = 2.0;       // grid spacing between paving tiles
const TILE_Y = 0.02;         // lay flat, just above terrain

// Seeded RNG for stable, non-flickering tile placement across re-renders
const seeded = (n) => {
  const x = Math.sin(n * 127.1 + 311.7) * 43758.5453;
  return x - Math.floor(x);
};

function buildPlaza() {
  const tiles = [];
  let idx = 0;
  const steps = Math.ceil(PLAZA_RADIUS / TILE_STEP);
  for (let i = -steps; i <= steps; i++) {
    for (let j = -steps; j <= steps; j++) {
      const x = i * TILE_STEP;
      const z = j * TILE_STEP;
      const r = Math.hypot(x, z);
      if (r > PLAZA_RADIUS || r < TRUNK_CLEAR) continue;
      idx += 1;
      const wide = seeded(idx * 2.3) > 0.18; // mostly wide tiles for full paving
      tiles.push({
        pos: [x + (seeded(idx) - 0.5) * 0.18, TILE_Y, z + (seeded(idx + 7) - 0.5) * 0.18],
        rot: [0, seeded(idx + 13) * Math.PI * 2, 0],
        scale: wide ? 1.05 : 1.0,
        wide,
      });
    }
  }
  return tiles;
}

export default function ThornhavenTownSquare() {
  const tree = useGLTF(URL.tree);
  const pathWide = useGLTF(URL.pathWide);
  const pathSmall = useGLTF(URL.pathSmall);
  const tiles = useMemo(buildPlaza, []);

  return (
    <group name="thornhaven-town-square">
      {/* Stone-paved plaza ring (open walking surface) */}
      {tiles.map((t, i) => (
        <Clone
          key={`plaza-${i}`}
          object={(t.wide ? pathWide : pathSmall).scene}
          position={t.pos}
          rotation={t.rot}
          scale={t.scale}
        />
      ))}

      {/* The great Yggdrasil - central landmark & coordinate anchor */}
      <group position={[0, 0, 0]} scale={TREE_SCALE}>
        <Clone object={tree.scene} position={TREE_OFFSET} />
      </group>

      {/* Collision: trunk base only (tight collar). Plaza stays fully walkable. */}
      <RigidBody type="fixed" colliders={false} position={[0, 0, 0]}>
        <CylinderCollider args={[6, 1.5]} position={[0, 6, 0]} />
      </RigidBody>
    </group>
  );
}
